// ============================================================================
// BOQ — splitting a sub-trade's number range across its trade types
// ============================================================================
// A sub-trade may carry a series of its own. Setting it, or pressing the
// refresh beside it, hands each trade type under that sub-trade a contiguous
// slice.
//
// A type may also ask for an exact count of numbers (the "No" column). Those
// are honoured to the number; whatever is left over is split equally among the
// types that asked for nothing. When every type has asked for a count and they
// do not use the whole range, the tail stays free — headroom for a type added
// later, rather than silently inflating someone's band past what they typed.
//
// The split runs only when a curator asks for it. Adding or deleting a type
// never re-splits on its own: a band that moved would start issuing numbers
// inside a band whose codes are already quoted in project BOQs, and an item
// code is immutable once saved.
//
// Pure, so the confirm dialog can show the caller exactly what a save would
// write before it writes it.
// ============================================================================

const isPositiveInt = (v) => Number.isInteger(v) && v > 0;

/**
 * Slice [start, end] into one contiguous band per trade type, in the order
 * given (the curator's drag order, which is what the page shows).
 *
 * @param start  first number in the sub-trade's range
 * @param end    last number, inclusive
 * @param types  [{ id, label, seriesWidth }] — seriesWidth null for "share the
 *               balance", or a positive integer for "exactly this many"
 * @returns { bands: [{ id, label, seriesStart, seriesEnd }] } or { error }
 */
export function distributeSeries({ start, end, types = [] }) {
  if (start == null || end == null) {
    return { error: 'A sub-trade range needs both a first and a last number.' };
  }
  const from = Number(start);
  const to = Number(end);
  if (!Number.isInteger(from) || !Number.isInteger(to)) {
    return { error: 'A sub-trade range needs a whole number at each end.' };
  }
  if (to < from) return { error: 'The end of a range cannot be below its start.' };

  // No types yet: the range is recorded and waits for them. Not an error —
  // setting the range before filing any type under it is the normal order.
  if (types.length === 0) return { bands: [] };

  const span = to - from + 1;

  const asked = types.filter((t) => t.seriesWidth != null);
  const bad = asked.find((t) => !isPositiveInt(Number(t.seriesWidth)));
  if (bad) {
    return { error: `"${bad.label}" asks for ${bad.seriesWidth} numbers. A count must be a whole number of 1 or more.` };
  }

  const fixedTotal = asked.reduce((sum, t) => sum + Number(t.seriesWidth), 0);
  if (fixedTotal > span) {
    return {
      error: `The counts add up to ${fixedTotal} numbers but ${formatRangeSize(span)} `
        + `— ${fixedTotal - span} short. Widen the range or lower a count.`,
    };
  }

  const sharing = types.filter((t) => t.seriesWidth == null);
  const balance = span - fixedTotal;
  if (sharing.length > balance) {
    return {
      error: `${balance} number${balance === 1 ? '' : 's'} left for `
        + `${sharing.length} trade types with no count. Widen the range, or give them counts.`,
    };
  }

  const base = sharing.length ? Math.floor(balance / sharing.length) : 0;
  let remainder = sharing.length ? balance % sharing.length : 0;

  let cursor = from;
  const bands = types.map((t) => {
    let width;
    if (t.seriesWidth != null) {
      width = Number(t.seriesWidth);
    } else {
      // The remainder goes to the earliest of the sharing types, so a balance
      // of 100 over 3 reads 34 / 33 / 33 rather than leaving a gap.
      width = base + (remainder > 0 ? 1 : 0);
      if (remainder > 0) remainder -= 1;
    }
    const band = { id: t.id, label: t.label, seriesStart: cursor, seriesEnd: cursor + width - 1 };
    cursor += width;
    return band;
  });

  return { bands };
}

const formatRangeSize = (span) => `the range only holds ${span}`;

/**
 * Whether a hand-edited band sits inside the sub-trade's range. The trigger
 * enforces this too; this is so the box can say no before a round trip.
 */
export function bandWithinSubtrade({ start, end, subStart, subEnd }) {
  if (subStart == null || subEnd == null) return null;
  if (start == null || end == null) return null;
  if (Number(start) < Number(subStart) || Number(end) > Number(subEnd)) {
    return `Outside the sub-trade's range.`;
  }
  return null;
}

const SERIES_MAX = 9999;

/**
 * Number the sub-trades below an edited one straight on from it. Each keeps
 * its own count; its start becomes the previous range's end plus one. A range
 * that would run past 9999 starts again at 0001 instead, since the item code
 * carries the sub-trade segment and a number reused under another sub-trade
 * never clashes. A sub-trade with no range is skipped and left empty: there
 * is no count to give it, and the next one carries on from the last range set.
 *
 * @param anchorEnd  last number of the edited sub-trade's new range
 * @param below      [{ id, label, seriesStart, seriesEnd }] in page order
 * @returns { moves: [{ id, label, from, to, oldFrom, oldTo }] } listing only
 *          the sub-trades whose range changes, or { error }
 */
/**
 * Where a range of `count` numbers starts when it follows on from a range
 * ending at `prevEnd`: the next number, or 0001 when there is nothing before
 * it (prevEnd null) or the range would run past 9999.
 */
export function nextSeriesStart(prevEnd, count) {
  if (prevEnd == null) return 1;
  const from = Number(prevEnd) + 1;
  return from + Number(count) - 1 > SERIES_MAX ? 1 : from;
}

export function cascadeSubtradeRanges({ anchorEnd, below = [] }) {
  let prevEnd = Number(anchorEnd);
  const moves = [];
  for (const sub of below) {
    if (sub.seriesStart == null || sub.seriesEnd == null) continue;
    const count = sub.seriesEnd - sub.seriesStart + 1;
    const from = nextSeriesStart(prevEnd, count);
    const to = from + count - 1;
    if (to > SERIES_MAX) {
      return { error: `"${sub.label}" holds ${count} numbers, more than 0001 to 9999 can fit.` };
    }
    if (from !== sub.seriesStart || to !== sub.seriesEnd) {
      moves.push({ id: sub.id, label: sub.label, from, to, oldFrom: sub.seriesStart, oldTo: sub.seriesEnd });
    }
    prevEnd = to;
  }
  return { moves };
}
